import React, { useState } from 'react';
import {
  Box,
  Button,
  Popper,
  Paper,
  Grid,
  Typography,
  Link as MuiLink,
  Fade,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * MegaMenu (desktop)
 *
 * Renders the configurable top-level navigation. Top-level items with children
 * open a mega-menu panel; children are grouped into columns by their
 * `column_heading`. A child carrying an `image` is rendered as a promo tile.
 */
const groupByColumn = (children = []) => {
  const cols = new Map();
  children.forEach((c) => {
    const key = c.column_heading || '';
    if (!cols.has(key)) cols.set(key, []);
    cols.get(key).push(c);
  });
  return Array.from(cols.entries()); // [ [heading, items], ... ]
};

const MegaMenu = ({ navigation = [] }) => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const go = (link) => {
    if (!link) return;
    setActiveId(null);
    if (/^https?:\/\//.test(link)) window.location.href = link;
    else navigate(link);
  };

  const handleEnter = (e, item) => {
    setActiveId(item.id);
    setAnchorEl(e.currentTarget);
  };

  const activeItem = navigation.find((n) => n.id === activeId);
  const promo = activeItem?.children?.find((c) => c.image);

  return (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'stretch', height: '100%' }} onMouseLeave={() => setActiveId(null)}>
      {navigation.map((item) => {
        const hasChildren = item.children && item.children.length > 0;
        return (
          <Box key={item.id} onMouseEnter={(e) => handleEnter(e, item)} sx={{ display: 'flex' }}>
            <Button
              onClick={() => go(item.link)}
              endIcon={hasChildren ? <KeyboardArrowDown sx={{ fontSize: 16 }} /> : null}
              sx={{
                color: item.highlight ? 'error.main' : 'text.primary',
                fontWeight: 600,
                px: 1.75,
                borderRadius: 0,
                height: '100%',
                '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
                ...(activeId === item.id && { color: 'primary.main' }),
              }}
            >
              {item.label}
            </Button>
          </Box>
        );
      })}

      <Popper
        open={Boolean(activeItem && activeItem.children && activeItem.children.length)}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        style={{ zIndex: 1200 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <Paper
              elevation={6}
              onMouseLeave={() => setActiveId(null)}
              sx={{ mt: 0, p: 3, borderRadius: 2, minWidth: 480, maxWidth: 860 }}
            >
              <Grid container spacing={3}>
                {groupByColumn((activeItem?.children || []).filter((c) => !c.image || c.column_heading)).map(
                  ([heading, items], idx) => (
                    <Grid item xs={6} sm={3} key={idx}>
                      {heading && (
                        <Typography
                          variant="overline"
                          sx={{ fontWeight: 700, color: 'text.secondary' }}
                        >
                          {heading}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
                        {items.map((c) => (
                          <MuiLink
                            key={c.id}
                            component="button"
                            underline="none"
                            onClick={() => go(c.link)}
                            sx={{
                              textAlign: 'left',
                              color: c.highlight ? 'error.main' : 'text.primary',
                              fontSize: '0.875rem',
                              '&:hover': { color: 'primary.main' },
                            }}
                          >
                            {c.label}
                          </MuiLink>
                        ))}
                      </Box>
                    </Grid>
                  )
                )}
                {promo && (
                  <Grid item xs={12} sm={4}>
                    <Box
                      onClick={() => go(promo.link)}
                      sx={{ cursor: 'pointer', borderRadius: 2, overflow: 'hidden' }}
                    >
                      <Box component="img" src={promo.image} alt={promo.label} sx={{ width: '100%', display: 'block' }} />
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        {promo.label}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

export default MegaMenu;
